import { useState, useRef, useCallback, useEffect } from 'react';

const useDraggable = ({
  initialPosition = { right: 0, bottom: 0 },
  onDrag = () => {},
  onDragStart = () => {},
  onDragEnd = () => {},
  getBounds = () => ({ right: Infinity, bottom: Infinity }),
  getCurrentPosition = null,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const startPositionRef = useRef(initialPosition);

  // Use refs for callbacks to avoid re-registering event listeners if callbacks are unstable
  const onDragRef = useRef(onDrag);
  const onDragStartRef = useRef(onDragStart);
  const onDragEndRef = useRef(onDragEnd);
  const getBoundsRef = useRef(getBounds);
  const getCurrentPositionRef = useRef(getCurrentPosition);

  useEffect(() => {
    onDragRef.current = onDrag;
    onDragStartRef.current = onDragStart;
    onDragEndRef.current = onDragEnd;
    getBoundsRef.current = getBounds;
    getCurrentPositionRef.current = getCurrentPosition;
  });

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartPos.current = { x: e.clientX, y: e.clientY };

    if (getCurrentPositionRef.current) {
      startPositionRef.current = getCurrentPositionRef.current();
    } else {
      startPositionRef.current = initialPosition;
    }

    onDragStartRef.current();
  }, [initialPosition]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;

    const dx = e.clientX - dragStartPos.current.x;
    const dy = e.clientY - dragStartPos.current.y;

    const newRight = startPositionRef.current.right - dx;
    const newBottom = startPositionRef.current.bottom - dy;

    const bounds = getBoundsRef.current();
    const clampedRight = Math.max(0, Math.min(newRight, bounds.right));
    const clampedBottom = Math.max(0, Math.min(newBottom, bounds.bottom));

    onDragRef.current({
      right: clampedRight,
      bottom: clampedBottom,
    });
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    onDragEndRef.current();
  }, [isDragging]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return {
    isDragging,
    handleMouseDown,
  };
};

export default useDraggable;
